import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { apiFetch } from "@/lib/api";

import type { KpiPeriod } from "./components/PeriodSelector";

export interface KpiArrSection {
  latest_arr: number;
  latest_month: string; // YYYY-MM
  latest_is_partial: boolean;
  anchor_arr: number;
  anchor_month: string;
  active_customers: number;
  booked_not_billed: number | null;
  booked_not_billed_accounts?: number;
  booked_not_billed_note: string;
  billed_plus_booked?: number | null;
  annualized_revenue: number | null;
  annualized_revenue_month: string;
  annualized_revenue_3mo_avg: number | null;
  ytd_revenue: number | null;
  ytd_revenue_start_month: string | null;
  ytd_revenue_end_month: string;
  /** Net new ARR for the window: new + PLG + PO + replacement delta − churn − contraction. */
  net_revenue: number;
  trend: { month: string; arr: number }[];
}

export interface KpiGrowthSeries {
  pct: number | null;
  delta_arr: number | null;
  vs_month: string | null;
  trend: number[];
}

export interface KpiUpsellSection {
  contract_delta_mrr: number;
  contract_delta_arr: number;
  expansion_arr: number;
  total_arr: number;
  contracts: number;
  new_logo_arr: number;
  prior_total_arr: number;
  prior_label: string;
}

export interface KpiChurnSection {
  churn_arr: number;
  contraction_arr: number;
  total_arr: number;
  net_expansion_arr: number;
}

export interface KpiNewBusinessSection {
  new_contracts: number;
  new_arr: number;
  plg_contracts: number;
  plg_arr: number;
  po_contracts: number;
  po_arr: number;
  total_contracts: number;
  /** Headline: Σ ARR of new + PLG contracts signed in the window. */
  total_arr: number;
  avg_deal_arr: number | null;
  /** From the waterfall: revenue + customers billing for the first time. */
  first_billed_arr: number;
  first_billed_customers: number;
  prior_label: string;
  prior_total_arr: number;
  prior_first_billed_arr: number;
  prior_first_billed_customers: number;
  total_arr_pct_change: number | null;
  first_billed_arr_pct_change: number | null;
  first_billed_customers_pct_change: number | null;
}

export interface KpiRetentionSection {
  available: boolean;
  anchor_month?: string;
  /** Net Revenue Retention — Σcurrent / Σprior_year, uncapped (>100% = expansion). */
  nrr_pct?: number | null;
  /** Gross Dollar Retention — Σmin(current,prior_year) / Σprior_year, ≤100%. */
  gdr_pct?: number | null;
  /** Σ prior-year MRR × 12 (the 12-mo-prior base). */
  base_arr?: number;
  /** Σ current MRR × 12 (NRR numerator). */
  current_arr?: number;
  /** Σ min(current, prior_year) × 12 (GDR numerator — "$-for-$ retained"). */
  retained_arr?: number;
  base_count?: number;
  /** Trailing-12-month NRR/GDR series for the drill-down chart. */
  series?: { month: string; nrr: number | null; gdr: number | null }[];
}

export type KpiStatusKey =
  | "active"
  | "in_onboarding"
  | "managed_by_reseller"
  | "churned"
  | "de_booked"
  | "m_and_a"
  | "other";

export type KpiStatusCounts = Record<KpiStatusKey, number>;

/** v7 SQO-cohort sales funnel (deal grain). Node totals plus the Booked /
 *  Not-Booked × customer-status cross-tab; every sankey link derives from
 *  these. `sqo` = `cohort_total`; "Billed" is intentionally absent (dropped
 *  from the funnel per the revops handoff, carried as a diagnostic only). */
export interface KpiSalesFunnel {
  new: number;
  existing: number;
  sqo: number;
  won: number;
  lost: number;
  still_open: number;
  booked: number;
  not_booked: number;
  status: KpiStatusCounts;
  status_by_chain: { booked: KpiStatusCounts; not_booked: KpiStatusCounts };
  cohort_total: number;
}

export interface KpiRate {
  key: string;
  label: string;
  value: number | null;
  prev: number | null;
  yoy: number | null;
  good_direction: "up" | "down";
  window_note: string;
  /** SQO→Won only: Min/Max bracket as still-open deals resolve, plus counts. */
  min?: number | null;
  max?: number | null;
  won?: number;
  lost?: number;
  closed?: number;
  still_open?: number;
  total?: number;
}

export interface KpiProductItem {
  key: string;
  name: string;
  color: string;
  arr: (number | null)[];
  accounts: (number | null)[];
  note: string | null;
}

export interface KpiDashboardResponse {
  period: KpiPeriod;
  window: {
    start: string;
    end_exclusive: string;
    months: string[];
    prior_label: string;
  };
  current_month: string;
  anchor_month: string;
  refreshed_at: string;
  arr: KpiArrSection;
  growth: { anchor_month: string; yoy: KpiGrowthSeries; mom: KpiGrowthSeries };
  new_business: KpiNewBusinessSection;
  upsell: KpiUpsellSection;
  churn: KpiChurnSection;
  retention: KpiRetentionSection;
  funnel: KpiSalesFunnel;
  rates: KpiRate[];
  products: { months: string[]; items: KpiProductItem[] };
}

export interface KpiMonthRange {
  /** YYYY-MM, both inclusive. */
  start: string | null;
  end: string | null;
}

// ─── retention drill-down ─────────────────────────────────────────────

export type RetentionOutcome = "expanded" | "flat" | "contracted" | "churned";
export type TierBasis = "base" | "current" | "initial";

export interface RetentionEntity {
  entity_id: number;
  name: string;
  members: { qb_id: number; name: string }[];
  base_arr: number;
  current_arr: number;
  retained_arr: number;
  delta_arr: number;
  delta_pct: number;
  outcome: RetentionOutcome;
  tier_base: number | null;
  tier_current: number | null;
  tier_initial: number | null;
  trajectory: { month: string; current_arr: number }[];
}

export interface KpiRetentionDetail {
  available: boolean;
  anchor_month?: string;
  tier_basis?: TierBasis;
  tiers?: number[] | null;
  totals?: {
    nrr_pct: number | null;
    gdr_pct: number | null;
    base_arr: number;
    current_arr: number;
    retained_arr: number;
    base_count: number;
  };
  series?: { month: string; nrr_pct: number | null; gdr_pct: number | null; base_arr: number }[];
  entities?: RetentionEntity[];
  refreshed_at?: string;
}

export function useRetentionDetail(
  period: KpiPeriod,
  range: KpiMonthRange | undefined,
  tierBasis: TierBasis,
  tiers: number[],
  enabled: boolean,
) {
  const tiersParam = tiers.length ? [...tiers].sort().join(",") : null;
  const ready = enabled && (period !== "CUSTOM" || !!(range?.start && range.end));
  return useQuery({
    queryKey: [
      "kpi", "retention-detail", period,
      range?.start ?? null, range?.end ?? null, tierBasis, tiersParam,
    ],
    queryFn: () => {
      const qs = new URLSearchParams({ period, tier_basis: tierBasis });
      if (period === "CUSTOM" && range?.start && range.end) {
        qs.set("start", range.start);
        qs.set("end", range.end);
      }
      if (tiersParam) qs.set("tiers", tiersParam);
      return apiFetch<KpiRetentionDetail>(`/kpi/detail/retention?${qs.toString()}`);
    },
    staleTime: 5 * 60 * 1000,
    enabled: ready,
  });
}

// ─── retention tier matrix (Customer Tiers → Retention tab) ───────────

export interface RetentionMatrixRow {
  key: string;
  label: string;
  tiers: number[] | null; // null = All (no tier filter)
  nrr_pct: number | null;
  gdr_pct: number | null;
  base_arr: number;
  current_arr: number;
  retained_arr: number;
  base_count: number;
}

export interface KpiRetentionMatrix {
  available: boolean;
  anchor_month?: string;
  tier_basis?: TierBasis;
  rows?: RetentionMatrixRow[];
  refreshed_at?: string;
}

export function useRetentionMatrix(
  period: KpiPeriod,
  range: KpiMonthRange | undefined,
  tierBasis: TierBasis,
  enabled: boolean,
) {
  const ready = enabled && (period !== "CUSTOM" || !!(range?.start && range.end));
  return useQuery({
    queryKey: [
      "kpi", "retention-matrix", period,
      range?.start ?? null, range?.end ?? null, tierBasis,
    ],
    queryFn: () => {
      const qs = new URLSearchParams({ period, tier_basis: tierBasis });
      if (period === "CUSTOM" && range?.start && range.end) {
        qs.set("start", range.start);
        qs.set("end", range.end);
      }
      return apiFetch<KpiRetentionMatrix>(`/kpi/detail/retention-matrix?${qs.toString()}`);
    },
    staleTime: 5 * 60 * 1000,
    enabled: ready,
  });
}

function dashboardQs(period: KpiPeriod, range?: KpiMonthRange): string {
  const qs = new URLSearchParams({ period });
  if (period === "CUSTOM" && range?.start && range.end) {
    qs.set("start", range.start);
    qs.set("end", range.end);
  }
  return qs.toString();
}

export function useKpiDashboard(period: KpiPeriod, range?: KpiMonthRange) {
  const ready = period !== "CUSTOM" || !!(range?.start && range.end);
  return useQuery({
    queryKey: ["kpi", "dashboard", period, range?.start ?? null, range?.end ?? null],
    queryFn: () =>
      apiFetch<KpiDashboardResponse>(`/kpi/dashboard?${dashboardQs(period, range)}`),
    staleTime: 5 * 60 * 1000,
    enabled: ready,
  });
}

/** Force-refresh: invalidates the server-side Snowflake cache, then replaces
 *  the local query data with the rebuilt payload. */
export function useKpiRefresh(period: KpiPeriod, range?: KpiMonthRange) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiFetch<KpiDashboardResponse>(`/kpi/refresh?${dashboardQs(period, range)}`, {
        method: "POST",
      }),
    onSuccess: (data) => {
      qc.setQueryData(
        ["kpi", "dashboard", period, range?.start ?? null, range?.end ?? null],
        data,
      );
      // Other periods share the underlying datasets — make them refetch.
      void qc.invalidateQueries({ queryKey: ["kpi", "dashboard"], exact: false });
    },
  });
}

// ─── New-business drill-down ──────────────────────────────────────────

export interface NewBusinessContract {
  contract_id: string | number | null;
  account_id: string | null;
  qb_id: number | null;
  account_name: string;
  kind: "new" | "plg" | "po";
  signed_date: string | null;
  arr: number;
}

export interface FirstBilledAccount {
  qb_id: number | null;
  account_id: string | null;
  account_name: string;
  month: string;
  arr: number;
}

export interface KpiNewBusinessDetail {
  available: boolean;
  totals?: KpiNewBusinessSection;
  contracts?: NewBusinessContract[];
  first_billed?: FirstBilledAccount[];
  refreshed_at?: string;
}

export function useNewBusinessDetail(
  period: KpiPeriod,
  range: KpiMonthRange | undefined,
  enabled: boolean,
) {
  const ready = enabled && (period !== "CUSTOM" || !!(range?.start && range.end));
  return useQuery({
    queryKey: ["kpi", "new-business-detail", period, range?.start ?? null, range?.end ?? null],
    queryFn: () =>
      apiFetch<KpiNewBusinessDetail>(`/kpi/detail/new_business?${dashboardQs(period, range)}`),
    staleTime: 5 * 60 * 1000,
    enabled: ready,
  });
}

// ─── Upsell drill-down ────────────────────────────────────────────────

export interface UpsellContract {
  contract_id: string | number | null;
  account_id: string | null;
  qb_id: number | null;
  account_name: string;
  kind: "replacement" | "upsell";
  signed_month: string;
  prior_arr: number;
  new_arr: number;
  uplift_arr: number;
}

export interface ExpansionAccount {
  qb_id: number | null;
  account_id: string | null;
  account_name: string;
  month: string;
  expansion_arr: number;
}

export interface KpiUpsellDetail {
  available: boolean;
  totals?: {
    contract_delta_arr: number;
    expansion_arr: number;
    total_arr: number;
    contracts: number;
  };
  contracts?: UpsellContract[];
  expansion?: ExpansionAccount[];
}

export function useUpsellDetail(
  period: KpiPeriod,
  range: KpiMonthRange | undefined,
  enabled: boolean,
) {
  const ready = enabled && (period !== "CUSTOM" || !!(range?.start && range.end));
  return useQuery({
    queryKey: ["kpi", "upsell-detail", period, range?.start ?? null, range?.end ?? null],
    queryFn: () => apiFetch<KpiUpsellDetail>(`/kpi/detail/upsell?${dashboardQs(period, range)}`),
    staleTime: 5 * 60 * 1000,
    enabled: ready,
  });
}

// ─── Churn & Contraction drill-down ───────────────────────────────────

export interface ChurnedAccount {
  qb_id: number | null;
  account_id: string | null;
  account_name: string;
  month: string;
  churn_arr: number;
  prior_arr: number;
}

export interface ContractedAccount {
  qb_id: number | null;
  account_id: string | null;
  account_name: string;
  month: string;
  contraction_arr: number;
  prior_arr: number;
  new_arr: number;
}

export interface KpiChurnDetail {
  available: boolean;
  totals?: {
    churn_arr: number;
    contraction_arr: number;
    total_arr: number;
    net_expansion_arr: number;
    churned_count: number;
    contracted_count: number;
  };
  churned?: ChurnedAccount[];
  contracted?: ContractedAccount[];
}

export function useChurnDetail(
  period: KpiPeriod,
  range: KpiMonthRange | undefined,
  enabled: boolean,
) {
  const ready = enabled && (period !== "CUSTOM" || !!(range?.start && range.end));
  return useQuery({
    queryKey: ["kpi", "churn-detail", period, range?.start ?? null, range?.end ?? null],
    queryFn: () => apiFetch<KpiChurnDetail>(`/kpi/detail/churn?${dashboardQs(period, range)}`),
    staleTime: 5 * 60 * 1000,
    enabled: ready,
  });
}

// ─── Sales funnel drill-down ──────────────────────────────────────────

export interface KpiFunnelDeal {
  deal_id: string | null;
  deal_name: string;
  amount: number | null;
  owner: string | null;
  outcome: string | null;
  chain: string | null;
  status: string | null;
  source: string | null;
  sqo_date: string | null;
  closed_at: string | null;
}

export interface KpiFunnelDetail {
  available: boolean;
  stage?: string;
  stage_label?: string;
  count?: number;
  total_amount?: number;
  deals?: KpiFunnelDeal[];
  refreshed_at?: string;
}

export function useFunnelDetail(
  period: KpiPeriod,
  range: KpiMonthRange | undefined,
  stage: string | null,
  enabled: boolean,
) {
  const ready =
    enabled && !!stage && (period !== "CUSTOM" || !!(range?.start && range.end));
  return useQuery({
    queryKey: ["kpi", "funnel-detail", period, range?.start ?? null, range?.end ?? null, stage],
    queryFn: () => {
      const qs = new URLSearchParams({ period, stage: stage ?? "" });
      if (period === "CUSTOM" && range?.start && range.end) {
        qs.set("start", range.start);
        qs.set("end", range.end);
      }
      return apiFetch<KpiFunnelDetail>(`/kpi/detail/funnel?${qs.toString()}`);
    },
    staleTime: 5 * 60 * 1000,
    enabled: ready,
  });
}

// ─── Booked-Not-Billed drill-down ─────────────────────────────────────

export interface BookedNotBilledAccount {
  qb_id: number | null;
  account_id: string | null;
  contract_id: string | number | null;
  account_name: string;
  contract_type: string | null;
  signed_at: string | null;
  days_waiting: number | null;
  arr: number;
}

export interface KpiBookedNotBilledDetail {
  available: boolean;
  count?: number;
  total_arr?: number;
  accounts?: BookedNotBilledAccount[];
  refreshed_at?: string;
}

/** Point-in-time snapshot — no period param. */
export function useBookedNotBilledDetail(enabled: boolean) {
  return useQuery({
    queryKey: ["kpi", "booked-not-billed-detail"],
    queryFn: () => apiFetch<KpiBookedNotBilledDetail>("/kpi/detail/booked_not_billed"),
    staleTime: 5 * 60 * 1000,
    enabled,
  });
}

// ─── Renewal rate (leading indicator, from the renewals feature) ──────

export interface RenewalsRetention {
  customer_retention_pct: number | null;
  arr_retention_pct: number | null;
  accounts_in_window: number;
  retained_count: number;
  lost_count: number;
  arr_at_stake: number;
  retained_arr: number;
  lost_arr: number;
  by_month: { month: string; retained: number; lost: number }[];
}

/** Trailing-12-month renewal rate from the renewals pipeline — a leading
 *  indicator of future TTM NRR/GDR. Needs renewals.read; the card degrades
 *  gracefully when the viewer lacks it. */
export function useRenewalRate(enabled: boolean) {
  return useQuery({
    queryKey: ["renewals", "dashboard", "retention"],
    queryFn: () => apiFetch<RenewalsRetention>("/renewals/dashboard/retention"),
    staleTime: 5 * 60 * 1000,
    enabled,
    retry: false,
  });
}

const MONTH_NAMES = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

/** "2026-05" → "May 2026" */
export function monthLabel(ym: string | null | undefined): string {
  if (!ym) return "—";
  const name = MONTH_NAMES[Number(ym.slice(5, 7)) - 1];
  return name ? `${name} ${ym.slice(0, 4)}` : ym;
}

/** "2026-05" → "May 26" (compact, for chart axes) */
export function monthLabelShort(ym: string): string {
  const name = MONTH_NAMES[Number(ym.slice(5, 7)) - 1];
  return name ? `${name} ${ym.slice(2, 4)}` : ym;
}
